import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "@/api/request";
import { AppRoutes } from "@/types/routes";
import {
  FileText,
  Upload,
  Shield,
  Zap,
  Target,
  Check,
  X,
  AlertTriangle,
  Lightbulb,
  Loader2,
  RotateCcw,
} from "lucide-react";
import logo from "@/assets/logo/1template.png";

type CheckState = "idle" | "uploading" | "checking" | "done" | "error";

interface QuickCheckResult {
  text_extractable: boolean;
  pages: number;
  char_count: number;
  sections_found: string[];
  contact: {
    has_name: boolean;
    has_email: boolean;
    has_phone: boolean;
    has_linkedin: boolean;
  };
  format_warnings: string[];
  content_warnings: string[];
  ats_friendly: boolean;
  score: number;
  time_ms: number;
}

const SESSION_ID = `ats${Date.now()}${Math.random().toString(36).slice(2, 9)}`;

export const AtsCheckerPage = () => {
  const [state, setState] = useState<CheckState>("idle");
  const [result, setResult] = useState<QuickCheckResult | null>(null);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const navigate = useNavigate();

  const resetScan = () => {
    setState("idle");
    setResult(null);
    setFileName("");
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.includes("pdf")) {
      setError("Please upload a PDF file.");
      setState("error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File must be under 5MB.");
      setState("error");
      return;
    }

    setFileName(file.name);
    setError("");
    setState("uploading");

    try {
      const buffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ""),
      );

      setState("checking");

      const data = await apiRequest<QuickCheckResult>(
        "/ats-checker/quick-check",
        "POST",
        {
          resume_pdf: base64,
          session_id: SESSION_ID,
        },
      );

      setResult(data);
      setState("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setState("error");
    }
  }, []);

  const handleDragIn = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length) setIsDragging(true);
  };

  const handleDragOut = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const scoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const scoreBg = (score: number) => {
    if (score >= 80) return "bg-green-50 border-green-200";
    if (score >= 60) return "bg-yellow-50 border-yellow-200";
    return "bg-red-50 border-red-200";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logo} alt="1Template" className="h-7" />
            <span className="text-lg font-semibold text-gray-900">ATS Scanner</span>
          </div>
          <button
            onClick={() => navigate(`/${AppRoutes.Login}`)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Sign in
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Is your resume ATS-friendly?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Upload your resume for a free, instant ATS compatibility score. We check formatting,
            sections, contact info, and content quality. No signup required.
          </p>
        </div>

        {/* Upload / Results */}
        {state === "done" && result ? (
          <div className="space-y-6">
            {/* Scan Another - prominent */}
            <div className="flex justify-end">
              <button
                onClick={resetScan}
                className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                <RotateCcw size={14} />
                Scan another resume
              </button>
            </div>

            {/* Score Card */}
            <div className={`rounded-xl border-2 p-8 text-center ${scoreBg(result.score)}`}>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                ATS Compatibility Score
              </p>
              <p className={`text-6xl font-bold ${scoreColor(result.score)}`}>
                {result.score}
                <span className="text-2xl text-gray-400">/100</span>
              </p>
              <p className="mt-2 text-gray-600">
                {result.ats_friendly
                  ? "Your resume is ATS-friendly!"
                  : "Your resume needs some work to pass ATS filters."}
              </p>
              <p className="text-sm text-gray-400 mt-1">{fileName}</p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Contact Info */}
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Contact Information</h3>
                <ul className="space-y-2">
                  {[
                    { label: "Name", ok: result.contact.has_name },
                    { label: "Email", ok: result.contact.has_email },
                    { label: "Phone", ok: result.contact.has_phone },
                    { label: "LinkedIn", ok: result.contact.has_linkedin },
                  ].map((item) => (
                    <li key={item.label} className="flex items-center gap-2 text-sm">
                      {item.ok ? (
                        <Check size={14} className="text-green-500" />
                      ) : (
                        <X size={14} className="text-red-400" />
                      )}
                      <span className={item.ok ? "text-gray-700" : "text-gray-500"}>
                        {item.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Sections Found */}
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Sections Detected</h3>
                {result.sections_found.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {result.sections_found.map((section) => (
                      <span
                        key={section}
                        className="px-2.5 py-1 bg-blue-50 text-blue-700 text-sm rounded-full capitalize"
                      >
                        {section}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No standard sections detected.</p>
                )}
              </div>

              {/* Format Warnings */}
              {result.format_warnings.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">Format Warnings</h3>
                  <ul className="space-y-1.5">
                    {result.format_warnings.map((warning, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-yellow-700">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span className="capitalize">{warning.replace(/_/g, " ")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Content Warnings */}
              {result.content_warnings.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">Content Suggestions</h3>
                  <ul className="space-y-1.5">
                    {result.content_warnings.map((warning, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-orange-700">
                        <Lightbulb size={14} className="mt-0.5 shrink-0" />
                        <span className="capitalize">{warning.replace(/_/g, " ")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-6 text-center">
              <h3 className="font-semibold text-gray-900 mb-2">
                Want a detailed scan with per-platform scoring?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Sign up free to get a full analysis across 6 ATS platforms, keyword matching,
                and personalized suggestions.
              </p>
              <button
                onClick={() => navigate(`/${AppRoutes.Signup}`)}
                className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Sign up for full scan
              </button>
            </div>
          </div>
        ) : (
          /* Upload Zone */
          <div
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => (state === "idle" || state === "error") && fileInputRef.current?.click()}
            className={`
              relative rounded-xl border-2 border-dashed p-16 text-center cursor-pointer transition-all
              ${isDragging ? "border-blue-400 bg-blue-50" : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"}
              ${state === "uploading" || state === "checking" ? "pointer-events-none" : ""}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileInput}
              className="hidden"
            />

            {state === "idle" && (
              <>
                <Upload size={40} className="mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-700 mb-1">
                  Drop your resume here or{" "}
                  <span className="text-blue-600 underline">choose a file</span>
                </p>
                <p className="text-sm text-gray-400">PDF only. Max 5MB.</p>
              </>
            )}

            {(state === "uploading" || state === "checking") && (
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={32} className="text-blue-600 animate-spin" />
                <p className="text-gray-600">
                  {state === "uploading" ? "Reading resume..." : "Scanning for ATS compatibility..."}
                </p>
                {fileName && <p className="text-sm text-gray-400">{fileName}</p>}
              </div>
            )}

            {state === "error" && (
              <div className="flex flex-col items-center gap-3">
                <AlertTriangle size={40} className="text-red-400" />
                <p className="text-red-600 font-medium">{error}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetScan();
                  }}
                  className="text-sm text-blue-600 underline"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        )}

        {/* Info Section */}
        {state === "idle" && (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <Shield size={24} className="mx-auto mb-2 text-gray-500" />
              <h3 className="font-semibold text-gray-900 mb-1">Private and Secure</h3>
              <p className="text-sm text-gray-500">
                Your resume is processed securely and not shared with third parties.
              </p>
            </div>
            <div className="text-center">
              <Zap size={24} className="mx-auto mb-2 text-gray-500" />
              <h3 className="font-semibold text-gray-900 mb-1">Instant Results</h3>
              <p className="text-sm text-gray-500">
                Get your ATS score in under a second. No waiting, no signup required.
              </p>
            </div>
            <div className="text-center">
              <Target size={24} className="mx-auto mb-2 text-gray-500" />
              <h3 className="font-semibold text-gray-900 mb-1">Actionable Feedback</h3>
              <p className="text-sm text-gray-500">
                See exactly what ATS systems look for and how your resume stacks up.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-20">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-400">
          &copy; {new Date().getFullYear()} 1Template. Free ATS Resume Scanner.
        </div>
      </footer>
    </div>
  );
};
